import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    if (!overflowList || overflowList.hasAttribute('static-visible')) return;

    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Finds a slide by media id inside a slideshow.
   * @param {Element} slideshow
   * @param {string} id
   * @returns {Element | null}
   */
  #findSlideInSlideshow(slideshow, id) {
    const idStr = String(id);
    const fromRefs = /** @type {{ slides?: Element[] } | undefined} */ (slideshow).refs?.slides?.find(
      (slide) => slide.getAttribute('slide-id') == idStr
    );
    if (fromRefs) return fromRefs;

    return (
      Array.from(slideshow.querySelectorAll('[slide-id]')).find((slide) => slide.getAttribute('slide-id') == idStr) ??
      null
    );
  }

  /**
   * @param {string} firstUrl
   * @param {string} secondUrl
   * @returns {boolean}
   */
  #isSameLinkedProductUrl(firstUrl, secondUrl) {
    const firstPath = new URL(firstUrl, window.location.origin).pathname.replace(/\/$/, '');
    const secondPath = new URL(secondUrl, window.location.origin).pathname.replace(/\/$/, '');

    return firstPath === secondPath || firstPath.startsWith(`${secondPath}-`) || secondPath.startsWith(`${firstPath}-`);
  }

  /**
   * @param {Element | null} linkedSlideshow
   * @param {string | null | undefined} slideId
   */
  #forceLinkedSlideshowPosition(linkedSlideshow, slideId) {
    if (!linkedSlideshow || !slideId) return;

    const scroller = linkedSlideshow.querySelector('slideshow-slides');
    const slide = Array.from(linkedSlideshow.querySelectorAll('[slide-id]')).find(
      (candidate) => candidate.getAttribute('slide-id') == slideId
    );
    if (!(scroller instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;

    for (const candidate of linkedSlideshow.querySelectorAll('[slide-id]')) {
      candidate.setAttribute('aria-hidden', candidate === slide ? 'false' : 'true');
      if (candidate instanceof HTMLElement) {
        candidate.style.contentVisibility = candidate === slide ? 'visible' : '';
      }
    }

    slide.hidden = false;
    slide.removeAttribute('hidden');
    slide.setAttribute('reveal', '');

    for (const image of slide.querySelectorAll('img')) {
      image.removeAttribute('loading');
      image.setAttribute('loading', 'eager');
      image.setAttribute('fetchpriority', 'high');
    }

    const align = () => {
      const left = slide.offsetLeft - scroller.offsetLeft;
      scroller.scrollTo({ left, top: 0, behavior: 'instant' });
    };

    align();
    requestAnimationFrame(align);
  }

  /**
   * Finds the combined-listing carousel for the selected linked product, or by media
   * id when no linked product identity is available.
   * @param {string} id
   * @param {string | null} [linkedProductId]
   * @param {string | null} [linkedProductUrl]
   * @param {string | null} [optionValue]
   * @returns {HTMLElement | null}
   */
  #findCombinedListingCarousel(id, linkedProductId = null, linkedProductUrl = null, optionValue = null) {
    const combinedCarousels = Array.from(this.querySelectorAll('.combined-listing-carousel')).filter(
      (carousel) => carousel instanceof HTMLElement
    );

    if (optionValue) {
      const normalizedOptionValue = optionValue.toLowerCase();
      const carousel = combinedCarousels.find(
        (carousel) =>
          carousel instanceof HTMLElement && carousel.dataset.productTitle?.toLowerCase().includes(normalizedOptionValue)
      );
      if (carousel instanceof HTMLElement) return carousel;
    }

    if (linkedProductUrl) {
      const carousel = combinedCarousels.find(
        (carousel) =>
          carousel instanceof HTMLElement &&
          carousel.dataset.linkedProductUrl &&
          this.#isSameLinkedProductUrl(carousel.dataset.linkedProductUrl, linkedProductUrl)
      );
      if (carousel instanceof HTMLElement) return carousel;
    }

    if (linkedProductId) {
      const carousel = combinedCarousels.find(
        (carousel) => carousel instanceof HTMLElement && carousel.dataset.linkedProductId == linkedProductId
      );
      if (carousel instanceof HTMLElement) return carousel;
    }

    if (id) {
      const idStr = String(id);
      for (const carousel of combinedCarousels) {
        if (!(carousel instanceof HTMLElement)) continue;
        const hasSlide = Array.from(carousel.querySelectorAll('[slide-id]')).some(
          (slide) => slide.getAttribute('slide-id') == idStr
        );
        if (hasSlide) return carousel;
      }
    }

    return null;
  }

  /**
   * Shows media for a swatch: prefer the main slideshow when the slide exists there,
   * otherwise fall back to the combined-listing carousel (CL child media is not always
   * injected into the parent gallery — never blank the card by hiding all slides).
   * @param {string | undefined} id
   * @param {string | null} [linkedProductId]
   * @param {string | null} [linkedProductUrl]
   * @param {string | null} [optionValue]
   * @returns {boolean}
   */
  #showMediaById(id, linkedProductId = null, linkedProductUrl = null, optionValue = null) {
    if (!id && !linkedProductId && !linkedProductUrl && !optionValue) return false;

    const { slideshow } = this.refs;
    const allCombinedCarousels = this.querySelectorAll('.combined-listing-carousel');
    const hasLinkedTarget = Boolean(linkedProductId || linkedProductUrl || optionValue);
    const foundCarousel = this.#findCombinedListingCarousel(id ?? '', linkedProductId, linkedProductUrl, optionValue);
    const mainSlide = id && slideshow ? this.#findSlideInSlideshow(slideshow, id) : null;

    if (mainSlide && slideshow && (!hasLinkedTarget || !foundCarousel)) {
      allCombinedCarousels.forEach((carousel) => {
        carousel.hidden = true;
      });
      slideshow.style.display = '';
      slideshow.select({ id }, undefined, { animate: false });
      this.#toggleTitleBlocks(id);
      return true;
    }

    if (!foundCarousel) {
      return false;
    }

    if (slideshow) slideshow.style.display = 'none';
    allCombinedCarousels.forEach((carousel) => {
      carousel.hidden = true;
    });
    foundCarousel.hidden = false;

    // Force layout after unhiding so the linked slideshow has dimensions for select/scroll.
    void foundCarousel.offsetHeight;

    const linkedSlideshow = foundCarousel.querySelector('slideshow-component');
    const linkedSlideId =
      id && linkedSlideshow && this.#findSlideInSlideshow(linkedSlideshow, id)
        ? id
        : foundCarousel.querySelector('[slide-id]')?.getAttribute('slide-id');

    if (
      linkedSlideId &&
      linkedSlideshow &&
      typeof /** @type {{ select?: Function }} */ (linkedSlideshow).select === 'function'
    ) {
      /** @type {{ select: Function }} */ (linkedSlideshow).select({ id: linkedSlideId }, undefined, { animate: false });
    }
    this.#forceLinkedSlideshowPosition(linkedSlideshow, linkedSlideId);

    this.#toggleTitleBlocks(id ?? '', linkedProductId, linkedProductUrl, optionValue);
    return true;
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    const selectedImageId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const linkedProductId =
      this.variantPicker?.selectedOption?.dataset.linkedProductId ?? this.variantPicker?.dataset?.productId ?? null;
    const linkedProductUrl =
      this.variantPicker?.selectedOption?.dataset.connectedProductUrl ??
      (!selectedImageId ? this.refs.productCardLink?.pathname : null) ??
      null;
    const optionValue = this.variantPicker?.selectedOption?.value ?? null;

    if (!selectedImageId && !linkedProductId && !linkedProductUrl && !optionValue) return;

    const mainSlide = selectedImageId && slideshow ? this.#findSlideInSlideshow(slideshow, selectedImageId) : null;

    // Only flip hidden flags when the target exists in the main slideshow.
    // Otherwise every variant slide would become hidden and the card goes blank
    // (common for combined listings when child media lives only in the CL carousel).
    if (mainSlide && slideshow?.refs?.slides) {
      for (const slide of slideshow.refs.slides) {
        const isVariantImage = slide.getAttribute('variant-image') != null;
        const isLinkedProductMedia = slide.getAttribute('linked-product-media') != null;

        if (!isVariantImage && !isLinkedProductMedia) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }
    }

    this.#showMediaById(selectedImageId, linkedProductId, linkedProductUrl, optionValue);
    if (selectedImageId) this.#updateTitleForSelectedVariant(selectedImageId);
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /** @type {string | null} */
  #selectedSwatchMediaId = null;

  /** @type {string | null} */
  #selectedSwatchLinkedProductId = null;

  /** @type {string | null} */
  #selectedSwatchLinkedProductUrl = null;

  /** @type {string | null} */
  #selectedSwatchValue = null;

  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant (main slideshow when possible, else CL carousel).
   * Hover callers pass a PointerEvent — only mouse hovers preview.
   * Selection/tap callers omit the event (or pass a non-pointer event) for immediate feedback.
   * @param {string} id - The id of the variant to preview.
   * @param {Event} [event] - Optional pointer event from hover handlers.
   */
  previewVariant(id, event) {
    // A programmatic call without an event is a deliberate tap/click selection preview.
    // Declarative hover events must identify themselves as mouse events; mobile browsers
    // can emit pointer enter/leave events with an empty pointerType during DOM replacement.
    if (event && event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();
    this.#showMediaById(id);
  }

  /**
   * Stores and previews a variant selected from the swatch picker.
   * @param {string | undefined} mediaId
   * @param {string | null} [linkedProductId]
   * @param {string | null} [linkedProductUrl]
   * @param {string | null} [optionValue]
   */
  selectVariant(mediaId, linkedProductId = null, linkedProductUrl = null, optionValue = null) {
    this.#selectedSwatchMediaId = mediaId ?? null;
    this.#selectedSwatchLinkedProductId = linkedProductId;
    this.#selectedSwatchLinkedProductUrl = linkedProductUrl;
    this.#selectedSwatchValue = optionValue;
    this.resetVariant.cancel();
    this.#showMediaById(mediaId, linkedProductId, linkedProductUrl, optionValue);
  }

  /**
   * Compact PLP grid (zoom-out): no hover image carousel — keep the primary swatch/image.
   * @returns {boolean}
   */
  #isZoomOutGridView() {
    const grid = this.closest('[product-grid-view]');
    return grid?.getAttribute('product-grid-view') === 'zoom-out';
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#isZoomOutGridView()) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') {
      return;
    }
    if (this.#isZoomOutGridView()) return;

    const { slideshow } = this.refs;
    if (!this.variantPicker) {
      if (!slideshow) {
        return;
      }
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
    
    // Reset title to original
    this.#resetTitleText();
  }

  /**
   * Resets the image to the selected (or initial) variant.
   * Uses main slideshow when possible, otherwise the CL carousel for that media id.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) {
      return;
    }

    const id = this.#selectedSwatchMediaId ?? this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const linkedProductId =
      this.#selectedSwatchLinkedProductId ??
      this.variantPicker?.selectedOption?.dataset.linkedProductId ??
      this.variantPicker?.dataset?.productId ??
      null;
    const linkedProductUrl =
      this.#selectedSwatchLinkedProductUrl ??
      this.variantPicker?.selectedOption?.dataset.connectedProductUrl ??
      (!id ? this.refs.productCardLink?.pathname : null) ??
      null;
    const optionValue = this.#selectedSwatchValue ?? this.variantPicker?.selectedOption?.value ?? null;

    if (id || linkedProductId || linkedProductUrl || optionValue) {
      if (this.#showMediaById(id, linkedProductId, linkedProductUrl, optionValue)) {
        if (id) this.#updateTitleForSelectedVariant(id);
        return;
      }
    }

    // No variant selected - show main slideshow and hide all carousels
    this.querySelectorAll('.combined-listing-carousel').forEach((carousel) => {
      carousel.hidden = true;
    });
    slideshow.style.display = '';

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');

    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      this.#resetTitleText();
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
    this.#resetTitleText();
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Updates the title text for linked products
   * @param {string} mediaId - The media ID of the variant to preview
   */
  #toggleTitleBlocks(mediaId, linkedProductId = null, linkedProductUrl = null, optionValue = null) {
    // First, check if there's a combined listing carousel for this media ID
    const allCombinedCarousels = this.querySelectorAll('.combined-listing-carousel');
    let foundCarousel = null;
    
    // Look for a carousel that contains this media ID, or belongs to the selected linked product.
    for (const carousel of allCombinedCarousels) {
      if (
        optionValue &&
        carousel instanceof HTMLElement &&
        carousel.dataset.productTitle?.toLowerCase().includes(optionValue.toLowerCase())
      ) {
        foundCarousel = carousel;
        break;
      }

      if (
        linkedProductUrl &&
        carousel instanceof HTMLElement &&
        carousel.dataset.linkedProductUrl &&
        this.#isSameLinkedProductUrl(carousel.dataset.linkedProductUrl, linkedProductUrl)
      ) {
        foundCarousel = carousel;
        break;
      }

      if (linkedProductId && carousel instanceof HTMLElement && carousel.dataset.linkedProductId == linkedProductId) {
        foundCarousel = carousel;
        break;
      }

      const slideshowElement = carousel.querySelector('slideshow-component');
      if (slideshowElement) {
        const slides = slideshowElement.querySelectorAll('[slide-id]');
        for (const slide of slides) {
          const slideId = slide.getAttribute('slide-id');
          if (slideId === mediaId.toString()) {
            foundCarousel = carousel;
            break;
          }
        }
        if (foundCarousel) break;
      }
    }
    
    if (!foundCarousel) {
      // No carousel found for this media ID - this is the main product, reset to original title
      this.#resetTitleText();
      return;
    }

    // Try to get product title from carousel data attributes
    let productTitle = foundCarousel.dataset.productTitle;
    
    // If no product title in carousel dataset, try to get it from the input element
    if (!productTitle) {
      const input = /** @type {HTMLInputElement | null} */ (
        this.querySelector(`input[data-option-media-id="${mediaId}"]`)
      );
      
      if (input) {
        productTitle = input.dataset.productTitle;
      }
    }
    
    if (productTitle) {
      this.#updateTitleText(productTitle);
    } else {
      this.#resetTitleText();
    }
  }

  /**
   * Updates the title text in the DOM
   * @param {string} newTitle - The new title to display
   */
  #updateTitleText(newTitle) {
    if (!this.refs.productTitleLink) {
      return;
    }

    const titleElement = this.refs.productTitleLink.querySelector('p');
    
    if (titleElement) {
      titleElement.textContent = newTitle;
    }
  }

  /**
   * Updates the title based on the selected variant
   * @param {string} selectedImageId - The media ID of the selected variant
   */
  #updateTitleForSelectedVariant(selectedImageId) {
    // Check if there's a combined listing carousel for this media ID
    const allCombinedCarousels = this.querySelectorAll('.combined-listing-carousel');
    let foundCarousel = null;
    
    // Look for a carousel that contains this media ID
    for (const carousel of allCombinedCarousels) {
      const slideshowElement = carousel.querySelector('slideshow-component');
      if (slideshowElement) {
        const slides = slideshowElement.querySelectorAll('[slide-id]');
        for (const slide of slides) {
          if (slide.getAttribute('slide-id') === selectedImageId.toString()) {
            foundCarousel = carousel;
            break;
          }
        }
        if (foundCarousel) break;
      }
    }
    
    if (foundCarousel) {
      // There's a carousel for this media ID - update title to the linked product title
      const productTitle = foundCarousel.dataset.productTitle;
      if (productTitle) {
        this.#updateTitleText(productTitle);
      } else {
        this.#resetTitleText();
      }
    } else {
      // No carousel found for this media ID - reset to original title
      this.#resetTitleText();
    }
  }

  /**
   * Resets the title to the original product title
   */
  #resetTitleText() {
    if (!this.refs.productTitleLink) {
      return;
    }

    const productTitle = this.dataset.productTitle || 'Product Title';
    this.#updateTitleText(productTitle);
  }

  /**
   * Resets the variant after hover leave. Mouse-only — touch enter/leave must not undo a tap.
   * @param {Event} [event]
   */
  resetVariant = debounce((event) => {
    // This handler is wired only to pointerleave. Require an explicit mouse pointer so
    // incomplete/synthetic mobile pointer events can never undo the selected swatch.
    if (event?.pointerType !== 'mouse') return;
    this.#resetVariant();
  }, 100);
}

if (!customElements.get('product-card')) {
  if (!customElements.get('product-card')) customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Immediate image feedback on click/tap (no pointer event → bypasses mouse-only hover guard).
    if (isSwatchInput && this.parentProductCard instanceof ProductCard) {
      const mediaId = clickedSwatch.dataset.optionMediaId;
      this.parentProductCard.selectVariant(
        mediaId,
        clickedSwatch.dataset.linkedProductId ?? null,
        clickedSwatch.dataset.connectedProductUrl ?? null,
        clickedSwatch.value
      );
    }

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  if (!customElements.get('swatches-variant-picker-component')) customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
